import React, { useMemo } from 'react';

import {
  CheckIcon,
  Combobox,
  Group,
  Pill,
  PillsInput,
  PillsInputProps,
  useCombobox,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';

export interface IPersistentSelectProps<T> {
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
  inputProps?: Omit<Partial<PillsInputProps>, 'onClick' | 'rightSection'>;
  placeholder?: string;
  emptyMessage?: string;
  getOptionId: (option: T) => string | number;
  getOptionLabel: (option: T) => string;
}

/** @registry Multi-select combobox with pill tags that persist as removable chips in the input (not just the dropdown); generic over any option type via getOptionId/getOptionLabel. */
export function PersistentSelect<T>({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  inputProps,
  emptyMessage = 'Nothing found...',
  getOptionId,
  getOptionLabel,
}: IPersistentSelectProps<T>) {
  const [search, setSearch] = React.useState('');

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const handleValueSelect = (val: string) => {
    const selectedOption = options.find(
      (option) => getOptionId(option).toString() === val,
    );
    if (selectedOption) {
      const newValue = value.some(
        (v) => getOptionId(v) === getOptionId(selectedOption),
      )
        ? value.filter((v) => getOptionId(v) !== getOptionId(selectedOption))
        : [...value, selectedOption];
      onChange(newValue);
    }
  };

  const handleValueRemove = (val: string) => {
    const newValue = value.filter((v) => getOptionId(v).toString() !== val);
    onChange(newValue);
  };

  const handleClear = () => {
    onChange([]);
    setSearch('');
    combobox.closeDropdown();
  };

  const values = useMemo(
    () =>
      value.map((item) => (
        <Pill
          key={getOptionId(item)}
          withRemoveButton
          onRemove={() => handleValueRemove(getOptionId(item).toString())}
        >
          {getOptionLabel(item)}
        </Pill>
      )),
    [value, handleValueRemove, getOptionId, getOptionLabel],
  );

  const filteredOptions = useMemo(
    () =>
      options
        .filter((item) =>
          getOptionLabel(item)
            .toLowerCase()
            .includes(search.trim().toLowerCase()),
        )
        .map((item) => (
          <Combobox.Option
            value={getOptionId(item).toString()}
            key={getOptionId(item)}
            active={value.some((v) => getOptionId(v) === getOptionId(item))}
          >
            <Group gap="sm">
              {value.some((v) => getOptionId(v) === getOptionId(item)) ? (
                <CheckIcon size={12} />
              ) : null}
              <span>{getOptionLabel(item)}</span>
            </Group>
          </Combobox.Option>
        )),
    [options, search, value, getOptionId, getOptionLabel],
  );

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      withinPortal={true}
    >
      <Combobox.DropdownTarget>
        <PillsInput
          {...inputProps}
          onClick={() => combobox.openDropdown()}
          rightSection={
            value.length > 0 ? (
              <BasicActionIcon
                variant="subtle"
                color="gray"
                onClick={handleClear}
                size="sm"
                label="Clear all selections"
              >
                <IconX size={16} />
              </BasicActionIcon>
            ) : null
          }
        >
          <Pill.Group>
            {values}

            <Combobox.EventsTarget>
              <PillsInput.Field
                onFocus={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
                value={search}
                placeholder={placeholder}
                onChange={(event) => {
                  combobox.updateSelectedOptionIndex();
                  setSearch(event.currentTarget.value);
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === 'Backspace' &&
                    search.length === 0 &&
                    value.length > 0
                  ) {
                    event.preventDefault();
                    handleValueRemove(
                      getOptionId(value[value.length - 1]).toString(),
                    );
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>
          <div
            style={{
              maxHeight: 350,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions
            ) : (
              <Combobox.Empty>{emptyMessage}</Combobox.Empty>
            )}
          </div>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
